<template>
  <div class="login-container">
    <div class="left-section">
      <div class="logo">
        <img :src="logoWhite" alt="Peelo Logo" />
      </div>
      <a href="https://peelo.io" class="back-link">
        <span>{{ $t('common.backToSite') }}</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      </a>
      <div class="hero-content">
        <TransitionGroup name="fade-slide">
          <div v-for="(slide, index) in slides" :key="'slide-' + index" v-show="currentSlide === index" class="slide">
            <h2>{{ slide.title }}</h2>
            <p>{{ slide.description }}</p>
          </div>
        </TransitionGroup>
        <div class="dots">
          <span 
            v-for="(slide, index) in slides" 
            :key="'dot-' + index"
            :class="['dot', { active: currentSlideIndex === index }]"
            @click="setSlide(index)"
          ></span>
        </div>
      </div>
    </div>
    <div class="right-section">
      <div class="auth-container">
        <div class="back-button-container">
          <router-link to="/login" class="back-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            <span>{{ $t('common.back') }}</span>
          </router-link>
        </div>

        <div v-if="!passwordReset">
          <h1>{{ $t('resetPassword.title') }}</h1>
          <p class="login-text">
            {{ $t('resetPassword.subtitle') }}
          </p>
          
          <form @submit.prevent="resetPassword" class="reset-password-form">
            <div class="form-group">
              <label for="password">{{ $t('resetPassword.newPassword') }}</label>
              <div class="password-input-container">
                <input 
                  :type="showPassword ? 'text' : 'password'" 
                  id="password" 
                  v-model="password" 
                  :placeholder="$t('resetPassword.newPasswordPlaceholder')"
                  required
                  :class="{ 'input-error': passwordError }"
                />
                <button 
                  type="button" 
                  class="password-toggle" 
                  @click="showPassword = !showPassword"
                >
                  <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                </button>
              </div>
              <div v-if="passwordError" class="field-error">
                {{ passwordError }}
              </div>
            </div>

            <div class="form-group">
              <label for="confirmPassword">{{ $t('resetPassword.confirmPassword') }}</label>
              <div class="password-input-container">
                <input 
                  :type="showConfirmPassword ? 'text' : 'password'" 
                  id="confirmPassword" 
                  v-model="confirmPassword" 
                  :placeholder="$t('resetPassword.confirmPasswordPlaceholder')"
                  required
                  :class="{ 'input-error': confirmPasswordError }"
                />
                <button 
                  type="button" 
                  class="password-toggle" 
                  @click="showConfirmPassword = !showConfirmPassword"
                >
                  <svg v-if="showConfirmPassword" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                </button>
              </div>
              <div v-if="confirmPasswordError" class="field-error">
                {{ confirmPasswordError }}
              </div>
            </div>

            <div class="password-requirements">
              <h4>{{ $t('resetPassword.requirements.title') }}</h4>
              <ul>
                <li :class="{ valid: requirements.length }">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                  {{ $t('resetPassword.requirements.length') }}
                </li>
                <li :class="{ valid: requirements.uppercase }">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                  {{ $t('resetPassword.requirements.uppercase') }}
                </li>
                <li :class="{ valid: requirements.lowercase }">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                  {{ $t('resetPassword.requirements.lowercase') }}
                </li>
                <li :class="{ valid: requirements.number }">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                  {{ $t('resetPassword.requirements.number') }}
                </li>
                <li :class="{ valid: requirements.special }">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                  {{ $t('resetPassword.requirements.special') }}
                </li>
              </ul>
            </div>
            
            <div v-if="errorMessage" class="error-message">
              {{ errorMessage }}
            </div>
            
            <button type="submit" class="submit-button" :disabled="isLoading || !isPasswordValid">
              <span v-if="isLoading" class="loader"></span>
              <span v-else>{{ $t('resetPassword.resetButton') }}</span>
            </button>
          </form>
        </div>
        
        <div v-else class="success-container">
          <div class="success-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#25D366" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <h3 class="success-title">{{ $t('resetPassword.successTitle') }}</h3>
          <p class="success-text">
            {{ $t('resetPassword.successMessage') }}
          </p>
          <button @click="goToLogin" class="back-to-login-button">
            {{ $t('auth.loginButton') }}
          </button>
        </div>

        <div v-if="!passwordReset" class="auth-footer">
          <div class="separator">
            <span>{{ $t('common.or') }}</span>
          </div>
          
          <div class="login-prompt">
            <span>{{ $t('auth.knowYourPassword') }}</span>
            <router-link to="/login" class="login-button">
              {{ $t('auth.loginButton') }}
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useRouter, useRoute } from "vue-router";
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue';
import logoWhite from '@/assets/logo-white.png';
import { useI18n } from 'vue-i18n';
import { axiosPeelo } from '@/utils/axios';

export default {
  name: 'ResetPassword',
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const currentSlideIndex = ref(0);
    const currentSlide = ref(0);
    const isTransitioning = ref(false);
    const isLoading = ref(false);
    const errorMessage = ref('');
    const password = ref('');
    const confirmPassword = ref('');
    const passwordReset = ref(false);
    const showPassword = ref(false);
    const showConfirmPassword = ref(false);
    const passwordError = ref('');
    const confirmPasswordError = ref('');
    
    const slides = computed(() => [
      {
        title: t('slides.slide1.title'),
        description: t('slides.slide1.description')
      },
      {
        title: t('slides.slide2.title'),
        description: t('slides.slide2.description')
      },
      {
        title: t('slides.slide3.title'),
        description: t('slides.slide3.description')
      }
    ]);

    let slideInterval;

    // Password requirements validation
    const requirements = computed(() => ({
      length: password.value.length >= 8,
      uppercase: /[A-Z]/.test(password.value),
      lowercase: /[a-z]/.test(password.value),
      number: /\d/.test(password.value),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password.value)
    }));

    const isPasswordValid = computed(() => {
      return Object.values(requirements.value).every(req => req) && 
             password.value === confirmPassword.value &&
             password.value.length > 0;
    });

    // Watch password changes for validation
    watch(password, (newPassword) => {
      passwordError.value = '';
      if (newPassword.length > 0 && !Object.values(requirements.value).every(req => req)) {
        passwordError.value = t('auth.errors.passwordNotMeetCriteria');
      }
    });

    // Watch confirm password changes
    watch(confirmPassword, (newConfirmPassword) => {
      confirmPasswordError.value = '';
      if (newConfirmPassword.length > 0 && newConfirmPassword !== password.value) {
        confirmPasswordError.value = t('auth.errors.passwordsNotMatch');
      }
    });

    const setSlide = (index) => {
      if (isTransitioning.value) return;
      
      isTransitioning.value = true;
      currentSlideIndex.value = index;
      
      setTimeout(() => {
        currentSlide.value = index;
        isTransitioning.value = false;
      }, 300);
    };

    const startSlideShow = () => {
      slideInterval = setInterval(() => {
        const nextIndex = (currentSlideIndex.value + 1) % slides.value.length;
        setSlide(nextIndex);
      }, 5000);
    };

    const resetPassword = async () => {
      if (!isPasswordValid.value) {
        errorMessage.value = t('auth.errors.fixErrors');
        return;
      }
      
      const token = route.query.token;
      if (!token) {
        errorMessage.value = t('auth.errors.invalidResetLink');
        return;
      }
      
      isLoading.value = true;
      errorMessage.value = '';
      
      try {
        const response = await axiosPeelo.post('/strong/auth/reset-password-confirm', {
          token: token,
          password: password.value,
          confirmPassword: confirmPassword.value
        });
        
        if (response.data && response.data.success) {
          passwordReset.value = true;
          isLoading.value = false;
        } else {
          throw new Error(response.data?.message || t('auth.errors.resetError'));
        }
      } catch (error) {
        isLoading.value = false;
        
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage.value = error.response.data.message;
        } else {
          errorMessage.value = t('auth.errors.resetError');
        }
      }
    };
    
    const goToLogin = () => {
      router.push('/login');
    };

    onMounted(() => {
      startSlideShow();
      
      // Vérifier si le token est présent dans l'URL
      const token = route.query.token;
      if (!token) {
        errorMessage.value = t('auth.errors.expiredResetLink');
      }
    });

    onBeforeUnmount(() => {
      clearInterval(slideInterval);
    });

    return {
      currentSlideIndex,
      currentSlide,
      isTransitioning,
      slides,
      setSlide,
      isLoading,
      errorMessage,
      password,
      confirmPassword,
      resetPassword,
      passwordReset,
      goToLogin,
      showPassword,
      showConfirmPassword,
      passwordError,
      confirmPasswordError,
      requirements,
      logoWhite,
      isPasswordValid
    };
  }
};
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background-color: #1a1a1a;
  overflow: hidden;
}

.left-section {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  margin: 1rem;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 2rem);
  max-height: calc(100vh - 2rem);
}

.left-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('@/assets/peelochat_ecommerce.png');
  background-size: cover;
  background-position: center;
  transition: transform 0.8s ease;
  z-index: 0;
}

.left-section:hover::before {
  transform: scale(1.1);
}

.left-section::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(37, 211, 102, 0.6), rgba(37, 211, 102, 0.2));
  z-index: 1;
}

.left-section > * {
  position: relative;
  z-index: 2;
}

.logo {
  position: relative;
  margin-bottom: 2rem;
}

.logo img {
  height: 40px;
  width: auto;
}

.back-link {
  position: absolute;
  top: 2rem;
  right: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(18, 140, 126, 0.2);
  border-radius: 20px;
  font-size: 14px;
  transition: background-color 0.2s;
}

.back-link:hover {
  background: rgba(18, 140, 126, 0.3);
}

.hero-content {
  margin-top: auto;
  margin-bottom: 3rem;
  color: white;
  position: relative;
  height: auto;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: visible;
}

.slide {
  padding: 0 3rem;
  position: absolute;
  width: 100%;
  overflow: visible;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.slide h2 {
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.slide p {
  font-size: 1.2rem;
  line-height: 1.5;
  opacity: 0.9;
  margin-bottom: 2rem;
}

.dots {
  display: flex;
  gap: 0.5rem;
  padding: 0 3rem;
  margin-top: 6rem;
  justify-content: center;
}

.dot {
  width: 2.5rem;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.dot:hover {
  background: rgba(255, 255, 255, 0.4);
}

.dot.active {
  background: white;
  width: 4rem;
}

.right-section {
  padding: 2rem 4rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow-y: auto;
  overflow-x: hidden;
}

.auth-container {
  max-width: 400px;
  width: 100%;
  overflow: hidden;
}

h1 {
  color: white;
  font-size: 2.5rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.login-text {
  color: #8f8f8f;
  margin-bottom: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  color: white;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.password-input-container {
  position: relative;
}

.form-group input {
  width: 100%;
  padding: 0.75rem;
  padding-right: 3rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: transparent;
  color: white;
  font-size: 1rem;
}

.form-group input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.3);
}

.input-error {
  border-color: #ff5252 !important;
}

.password-toggle {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-toggle:hover {
  color: rgba(255, 255, 255, 0.8);
}

.field-error {
  color: #ff5252;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  animation: fadeIn 0.3s ease;
}

.password-requirements {
  margin: 1.5rem 0;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.password-requirements h4 {
  color: white;
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
  font-weight: 500;
}

.password-requirements ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.password-requirements li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
  margin-bottom: 0.5rem;
  transition: color 0.2s ease;
}

.password-requirements li:last-child {
  margin-bottom: 0;
}

.password-requirements li svg {
  opacity: 0.3;
  transition: opacity 0.2s ease;
}

.password-requirements li.valid {
  color: #25D366;
}

.password-requirements li.valid svg {
  opacity: 1;
}

.error-message {
  color: #ff5252;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: rgba(255, 82, 82, 0.1);
  border-radius: 8px;
  border-left: 3px solid #ff5252;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.submit-button {
  width: 100%;
  padding: 0.75rem;
  border: none;
  border-radius: 8px;
  background: white;
  color: #1a1a1a;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.submit-button:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-1px);
}

.submit-button:active {
  transform: translateY(0);
}

.submit-button:disabled {
  background: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
  transform: none;
}

.loader {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(26, 26, 26, 0.3);
  border-radius: 50%;
  border-top-color: #1a1a1a;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.auth-footer {
  margin-top: 2rem;
}

.separator {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 2rem 0;
  color: #8f8f8f;
}

.separator::before,
.separator::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.separator span {
  padding: 0 10px;
}

.login-prompt {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 1.5rem 0;
}

.login-prompt span {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
}

.login-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: rgba(37, 211, 102, 0.1);
  border-radius: 8px;
  color: #25D366;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.login-button:hover {
  background: rgba(37, 211, 102, 0.2);
  transform: translateX(5px);
}

.login-button svg {
  transition: transform 0.2s ease;
}

.login-button:hover svg {
  transform: translateX(3px);
}

.reset-password-form {
  margin-top: 1rem;
}

.success-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2rem 0;
  animation: fadeIn 0.5s ease;
}

.success-icon {
  margin-bottom: 1.5rem;
}

.success-title {
  color: #25D366;
  font-size: 1.5rem;
  margin-bottom: 1rem;
}

.success-text {
  color: #8f8f8f;
  margin-bottom: 2rem;
  line-height: 1.5;
}

.back-to-login-button {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  background: white;
  color: #1a1a1a;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.back-to-login-button:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-1px);
}

.back-button-container {
  margin-bottom: 1.5rem;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  padding: 0.5rem 0;
}

.back-button:hover {
  color: white;
  transform: translateX(-5px);
}

.back-button svg {
  transition: transform 0.2s ease;
}

.back-button:hover svg {
  transform: translateX(-3px);
}

.fade-slide-enter-active {
  transition: all 0.8s ease-out;
  transition-delay: 0.2s;
}

.fade-slide-leave-active {
  transition: all 0.6s ease-in;
  position: absolute;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(30px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

@media (max-width: 768px) {
  .login-container {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }

  .right-section {
    padding: 2rem;
    min-height: 100vh;
  }

  .auth-container {
    margin: 0 auto;
    padding: 0 1rem;
  }

  h1 {
    font-size: 2rem;
  }

  .slide h2 {
    font-size: 2rem;
  }
}
</style>